import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import AuthLayout from '../common/AuthLayout';
import FeatureList from '../common/FeatureList';
import Button from '../ui/Button';

const features = [
  {
    icon: (
      <svg
        className="w-6 h-6 text-red-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
    title: 'Contact Your Administrator',
    description:
      "Reach out to your company's compliance administrator to request access",
  },
  {
    icon: (
      <svg
        className="w-6 h-6 text-red-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    ),
    title: 'Provide Your Details',
    description:
      'Share your email address and role with your administrator',
  },
  {
    icon: (
      <svg
        className="w-6 h-6 text-red-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    title: 'Get Access',
    description:
      "Once registered, you'll be able to sign in successfully",
  },
];

function PersonnelNotFoundScreen() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      await signOut();
      navigate('/signin', { replace: true });
    } catch (error) {
      setIsSigningOut(false);
      navigate('/signin', { replace: true });
    }
  };

  const leftContent = (
    <>
      <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
        Access Restricted 🔒
      </h1>
      <p className="text-lg md:text-xl mb-8 text-gray-700">
        Your email address is not registered in our personnel records. Please
        contact your administrator to request access.
      </p>
      <FeatureList features={features} />
    </>
  );

  const rightContent = (
    <>
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-6">
          <svg
            className="w-10 h-10 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
          Access Not Available
        </h2>

        <p className="text-base text-gray-600 mb-2">
          You are not registered in our personnel records.
        </p>
        <p className="text-base text-gray-600 mb-4">
          Please contact your administrator to request access to Compliance Hub.
        </p>
      </div>

      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-6">
        <div className="flex items-start">
          <svg
            className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0 text-red-600"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-800 mb-2">
              What to do next:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-red-700">
              <li>Contact your company's compliance administrator</li>
              <li>Provide your email address and role</li>
              <li>Request to be added to the personnel records</li>
              <li>Once registered, try signing in again</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <Button
          variant="danger"
          size="lg"
          onClick={handleSignOut}
          className="w-full"
          disabled={isSigningOut}
          icon={
            isSigningOut ? null : (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
            )
          }
        >
          {isSigningOut ? 'Signing out...' : 'Return to Sign In'}
        </Button>
      </div>

      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-600 text-center">
          <strong>Need help?</strong> If you believe this is an error, please
          contact your administrator with your email address.
        </p>
      </div>
    </>
  );

  return (
    <AuthLayout
      leftContent={leftContent}
      rightContent={rightContent}
      gradientFrom="from-red-50"
      gradientVia="via-orange-50"
      gradientTo="to-red-100"
    />
  );
}

export default PersonnelNotFoundScreen;

